import { focusComposedPath } from "../common/helpers";
const SHORTCUTS = new Map();
let LATEST_SHORTCUT;
export function loadShortcuts(name, root, shortcuts) {
    shortcuts.forEach(shortcut => {
        const keyShortcuts = parseKeyShortcuts(shortcut.keyShortcuts);
        keyShortcuts.forEach(keyShortcut => {
            const normalizedKeyShortcut = normalize(keyShortcut.ctrl, keyShortcut.alt, keyShortcut.shift, keyShortcut.meta, keyShortcut.key);
            SHORTCUTS.set(normalizedKeyShortcut, {
                name,
                root,
                shortcut
            });
        });
    });
    addListener();
}
export function unloadShortcuts(name) {
    const removeKeyShortcuts = [];
    SHORTCUTS.forEach((shortcutMap, key) => {
        if (shortcutMap.name === name) {
            removeKeyShortcuts.push(key);
        }
    });
    removeKeyShortcuts.forEach(key => SHORTCUTS.delete(key));
    removeListener();
}
export function getShortcuts(name) {
    return Array.from(SHORTCUTS.values())
        .filter(shortcutMap => {
        return (shortcutMap.name === name &&
            !shortcutMap.shortcut.conditions?.focusInclude);
    })
        .map(shortcutMap => ({
        element: querySelectorPlus(shortcutMap.shortcut.selector, shortcutMap.root),
        keyShortcuts: shortcutMap.shortcut.keyShortcuts,
        legendPosition: shortcutMap.shortcut.legendPosition
    }));
}
function addListener() {
    if (SHORTCUTS.size > 0) {
        window.addEventListener("keydown", keydownHandler, { capture: true });
    }
}
function removeListener() {
    if (SHORTCUTS.size === 0) {
        window.removeEventListener("keydown", keydownHandler, { capture: true });
    }
}
function keydownHandler(eventInfo) {
    if (!eventInfo.repeat ||
        (eventInfo.repeat && LATEST_SHORTCUT?.shortcut.conditions?.allowRepeat)) {
        LATEST_SHORTCUT = triggerShortcut(eventInfo);
    }
}
function triggerShortcut(eventInfo) {
    const shortcut = normalize(eventInfo.ctrlKey, eventInfo.altKey, eventInfo.shiftKey, eventInfo.metaKey, eventInfo.key);
    const shortcutMap = SHORTCUTS.get(shortcut);
    const focus = focusComposedPath();
    if (shortcutMap && conditions(shortcutMap, focus)) {
        const element = querySelectorPlus(shortcutMap.shortcut.selector, shortcutMap.root);
        const keyShortcutPressedEvent = createEvent(shortcut, shortcutMap.shortcut.id, element, focus);
        if (shortcutMap.root.dispatchEvent(keyShortcutPressedEvent)) {
            switch (shortcutMap.shortcut.action) {
                case "click":
                    element?.dispatchEvent(new Event("click"));
                    break;
                default:
                    element?.focus();
            }
            if (shortcutMap.shortcut.preventDefault !== false) {
                eventInfo.preventDefault();
            }
        }
        else {
            eventInfo.preventDefault();
        }
    }
    return shortcutMap;
}
function parseKeyShortcuts(value = "") {
    return value.split(/(?<!(?:[+]|^))\s/).map(item => {
        return item.split(/(?<!(?:[+]|^))[+]/).reduce((keyShortcut, key) => {
            switch (key.toLowerCase()) {
                case "ctrl":
                    keyShortcut.ctrl = true;
                    break;
                case "alt":
                    keyShortcut.alt = true;
                    break;
                case "shift":
                    keyShortcut.shift = true;
                    break;
                case "meta":
                    keyShortcut.meta = true;
                    break;
                default:
                    keyShortcut.key = key;
            }
            return keyShortcut;
        }, {
            ctrl: false,
            alt: false,
            shift: false,
            meta: false,
            key: ""
        });
    });
}
function normalize(ctrl, alt, shift, meta, key) {
    return [
        ctrl ? "Ctrl" : null,
        alt ? "Alt" : null,
        shift ? "Shift" : null,
        meta ? "Meta" : null,
        key ? key.charAt(0).toUpperCase() + key.slice(1) : null
    ]
        .filter(element => {
        return element !== null;
    })
        .join("+");
}
function conditions(shortcutMap, focus) {
    if (shortcutMap.shortcut.conditions?.focusInclude) {
        return querySelectorAllPlus(shortcutMap.shortcut.conditions.focusInclude, shortcutMap.root).some((el) => focus.includes(el));
    }
    if (shortcutMap.shortcut.conditions?.focusExclude) {
        return !querySelectorAllPlus(shortcutMap.shortcut.conditions.focusExclude, shortcutMap.root).some((el) => focus.includes(el));
    }
    return true;
}
function createEvent(keyShortcut, id, target, focusComposedPath) {
    return new CustomEvent("keyShortcutPressed", {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
            keyShortcut,
            id: id,
            target,
            focusComposedPath
        }
    });
}
function querySelectorAllPlus(selector, root) {
    return (selector
        ?.split(",")
        .map(selectorItem => {
        if (selector.includes("::part")) {
            return querySelectorPlus(selectorItem, root);
        }
        else {
            return Array.from(root.querySelectorAll(selector));
        }
    })
        .flat() ?? []);
}
function querySelectorPlus(selector, root) {
    const querySelectorDeep = (element, parts) => {
        const shadow = element.shadowRoot;
        const partList = parts.split(" ");
        const partElement = shadow.querySelector(partList.map(partName => `[part~="${partName}"]`).join(""));
        if (partElement) {
            return partElement;
        }
        const exportPartElement = shadow.querySelector(partList.map(partName => `[exportparts*="${partName}"]`).join(""));
        if (exportPartElement) {
            const exportPartList = [];
            const exportparts = exportPartElement.getAttribute("exportparts");
            partList.forEach(partItem => {
                const exportPartName = exportparts.match(`(?:([\\w-]+):)?(${partItem})`)[1];
                if (exportPartName) {
                    exportPartList.push(exportPartName);
                }
            });
            if (partList.length === exportPartList.length) {
                return querySelectorDeep(exportPartElement, exportPartList.join(" "));
            }
        }
        return null;
    };
    if (selector?.includes("::part")) {
        const selectorItems = selector.match("(.*)::part\\(([^)]+)\\)");
        const entity = selectorItems[1];
        const partName = selectorItems[2];
        return querySelectorDeep(root.querySelector(entity), partName);
    }
    else {
        return root.querySelector(selector);
    }
}
//# sourceMappingURL=ch-shortcuts-manager.js.map
